import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { PAGE_SIZE } from '../api/reportingApiService'
import { PaginationSelectOption } from '../composites/GamesOverview/components/PaginationControls'
import { noop } from '../util/functional'
import { BadgeInfo, GameReportSummaryData, PlayerDataRow, TaskDataRow } from './ReportsContextHelper'
import { useGame } from './GameContext'
import { AnswerStateEnum, ObjectAny } from '../types/commonTypes'
import { getPlayerStateOverview } from '../pages/GameEditor/components/EditorSidebar/Play/helpers'
import { getTitleForTask } from '../pages/GameEditor/components/Tasks/TaskModal/helpers'
import { useTranslation } from 'react-i18next'
import { getCompletedAndEvaluatedCounts, getLastActionTime, getSumCompletionTime } from './GameReportContextHelper'
import { ExerciseType } from '../api/gameTypes'

export const PAGE_SIZE_OPTIONS = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
]

export type PlayerFilters = {
  completion?: 'all' | 'completed' | 'uncompleted'
  badgeId?: number
  startDate?: Date
  endDate?: Date
}

export type PlayerSortOption = keyof Omit<PlayerDataRow, 'awardedBadges'>

export type TaskSortOption = keyof Omit<TaskDataRow, 'type'>

export enum ReportSortDir {
  ASC = ' asc',
  DESC = ' desc',
}

export interface GameReportDataProviderInterface {
  summaryData?: GameReportSummaryData
  currentPage: number
  pageSize: number
  pageData: PlayerDataRow[] | undefined
  tasksData: TaskDataRow[] | undefined
  loading: boolean
  lastPage: number
  allPageOptions: PaginationSelectOption[]
  selectPageSize: (newPageSize: number) => void
  prev: () => void
  next: () => void
  setPage: (newPage: number) => void
  playerSortBy?: PlayerSortOption
  taskSortBy?: TaskSortOption
  playerSortDir?: ReportSortDir
  taskSortDir?: ReportSortDir
  setPlayerSort?: (sortBy: PlayerSortOption, sortDir: ReportSortDir) => void
  setTaskSort?: (sortBy: TaskSortOption, sortDir: ReportSortDir) => void
  playerFilters: PlayerFilters
  setPlayerFilters: (filters: PlayerFilters) => void
  fetchSummaryData?: (gameId: number) => void
  fetchPlayersData?: (gameId: number, fromDate?: string, untilDate?: string) => Promise<PlayerDataRow[]>
  getAllPlayerDataRows?: () => PlayerDataRow[]
  playerCount: number
}

const GameReportDataContext = createContext<GameReportDataProviderInterface>({
  summaryData: undefined,
  currentPage: 1,
  pageSize: PAGE_SIZE,
  pageData: undefined,
  tasksData: undefined,
  loading: true,
  lastPage: 1,
  allPageOptions: [] as PaginationSelectOption[],
  selectPageSize: noop,
  prev: noop,
  next: noop,
  setPage: noop,
  playerSortBy: 'rank',
  playerSortDir: ReportSortDir.ASC,
  taskSortBy: 'name',
  taskSortDir: ReportSortDir.ASC,
  setPlayerSort: noop,
  setTaskSort: noop,
  playerFilters: {},
  setPlayerFilters: (filters: PlayerFilters) => {},
  playerCount: 0,
})

interface Props {
  gameId: number
  children?: React.ReactNode
}

const paginate = (
  arrayData: ObjectAny,
  pageSize: number,
  currentPage: number,
  sortBy: string,
  sortDir: ReportSortDir,
): PlayerDataRow[] | TaskDataRow[] => {
  return arrayData
    .sort((a: any, b: any) => {
      const aValue: number | string = typeof a[sortBy] !== 'string' && isNaN(a[sortBy]) ? '0' : a[sortBy] ?? 0
      const bValue: number | string = typeof a[sortBy] !== 'string' && isNaN(b[sortBy]) ? '0' : b[sortBy] ?? 0
      return sortDir === ReportSortDir.ASC ? (aValue > bValue ? 1 : -1) : aValue < bValue ? 1 : -1
    })
    .slice(pageSize * (currentPage - 1), pageSize * currentPage)
}

export const GameReportDataProvider: React.FC<Props> = ({ gameId, children }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE)
  const [loading] = useState(false)
  const [allPageOptions, setAllPageOptions] = useState<PaginationSelectOption[]>([{ value: 1, label: '1' }])
  const [playerSortBy, setPlayerSortBy] = useState<PlayerSortOption>('rank')
  const [playerSortDir, setPlayerSortDir] = useState<ReportSortDir>(ReportSortDir.ASC)
  const [taskSortBy, setTaskSortBy] = useState<TaskSortOption>('name')
  const [taskSortDir, setTaskSortDir] = useState<ReportSortDir>(ReportSortDir.ASC)
  const [playerFilters, setPlayerFilters] = useState<PlayerFilters>({})
  const { gameData, people, tasks } = useGame()

  const playersInDateRange = useMemo(() => {
    return people.players.filter(
      (p) =>
        (playerFilters.startDate === undefined || new Date(p.createdAt) > playerFilters.startDate) &&
        (playerFilters.endDate === undefined || new Date(p.createdAt) < playerFilters.endDate),
    )
  }, [people.players, playerFilters.endDate, playerFilters.startDate])
  const activePlayerIds = useMemo(() => playersInDateRange.map((p) => p.id), [playersInDateRange])

  const [playerCount, setPlayerCount] = useState(playersInDateRange.filter((p) => !p.deleted).length)
  const [lastPage, setLastPage] = useState(Math.ceil(playersInDateRange.filter((p) => !p.deleted).length / pageSize))
  const { t } = useTranslation()

  const tasksData = useMemo((): TaskDataRow[] | undefined => {
    if (!gameData || !tasks) {
      return undefined
    }
    const temp: TaskDataRow[] = tasks.map((task) => {
      const answerCount = task.subtasks[0]?.answers?.filter((a) => activePlayerIds.includes(a.userId)).length ?? 0
      const { completedCount, evaluatedCount } = getCompletedAndEvaluatedCounts(playersInDateRange, task)
      let totalPointsFromTask = 0
      let maxPoints = 0
      task.subtasks.forEach((t) => {
        t.answers
          ?.filter((a) => activePlayerIds.includes(a.userId))
          .forEach((a) => {
            totalPointsFromTask += a.points
            maxPoints += t.type === ExerciseType.PollExercise && t.data.hasTotalPoints
                  ? t.data.totalPoints
                  : t.maxPoints
          })
      })
      const sumCompletionTime = getSumCompletionTime(task, activePlayerIds)
      const averagePoints = Math.ceil(totalPointsFromTask / answerCount)
      const maxPoints2 = task.subtasks
        .map((t) => (t.type === ExerciseType.PollExercise && t.data.hasTotalPoints ? t.data.totalPoints : t.maxPoints))
        .reduce((a, c) => {
          return a + c
        }, 0)
      const averageScorePercentage = Math.ceil((100 * totalPointsFromTask) / maxPoints)
      const averageTime = Math.ceil(sumCompletionTime / 1000 / answerCount)
      return {
        task,
        id: task.id,
        name: task.name,
        type: getTitleForTask(task, t),
        rawType: task.type,
        answerCount,
        maxPoints: maxPoints2,
        completedCount,
        evaluatedCount,
        averagePoints,
        averageScorePercentage,
        averageTime,
        lastActionTime: getLastActionTime(task),
      }
    })
    return paginate(temp, 200, 1, taskSortBy, taskSortDir) as TaskDataRow[]
  }, [gameData, playersInDateRange, t, taskSortBy, taskSortDir, tasks, activePlayerIds])

  const getAllPlayerDataRows = useCallback((): PlayerDataRow[] => {
    if (!gameData || !tasks || !playersInDateRange) {
      return []
    }
    const playerData = playersInDateRange
      .map((p) => {
        //Need to override the player's points here as they are not updated in poeple players realtime
        let totalPoints = 0
        tasks.forEach((task) => {
          task.subtasks.forEach((subtask) => {
            const playerAnswer = subtask.answers?.filter((a) => a.userId === p.id)
            totalPoints = totalPoints + (playerAnswer && playerAnswer[0] ? playerAnswer[0].points : 0)
          })
        })
        return {
          ...p,
          points: totalPoints,
        }
      })
      .filter((p) => !p.deleted)
      .sort((a, b) => b.points - a.points)
      .map((player, index) => {
        let totalSpentTime = 0
        let totalAnswersCount = 0
        tasks.forEach((task) => {
          const answerTime = task.subtasks[0].answers
            ?.filter((a) => a.userId === player.id && a.state !== AnswerStateEnum.REVISION)
            ?.map((a) => {
              return new Date(a.completedAt).getTime() - new Date(a.openedAt).getTime()
            })
          if (answerTime && answerTime.length > 0) {
            totalAnswersCount++
            totalSpentTime += answerTime[0] / 1000
          }
        })
        const status = getPlayerStateOverview(player.id, tasks, gameData)
        return {
          rank: index + 1,
          nickName: player.nickName,
          email: player.email,
          teamMemberNames: player.teamMemberNames,
          points: player.points,
          correctPercentage: Math.min((100 * player.points) / gameData.maxPoints, 100),
          gameDuration: totalSpentTime,
          completionRate: Math.ceil(Math.min((100 * totalAnswersCount) / tasks.length, 100)),
          awardedBadges: player.badges,
          overallCompleted: status.gameCompleted,
          createdAt: player.createdAt,
          completedAt: (player.completedAt ?? '').trim().length > 10 ? (player.completedAt ?? '').trim() : undefined,
        }
      })
    return playerData
  }, [gameData, playersInDateRange, tasks])

  const pageData = useMemo((): PlayerDataRow[] | undefined => {
    if (!gameData || !tasks || !playersInDateRange) {
      return undefined
    }
    const playerData = playersInDateRange
      .map((p) => {
        //Need to override the player's points here as they are not updated in poeple players realtime
        let totalPoints = 0
        tasks.forEach((task) => {
          task.subtasks.forEach((subtask) => {
            const playerAnswer = subtask.answers?.filter((a) => a.userId === p.id)
            totalPoints = totalPoints + (playerAnswer && playerAnswer[0] ? playerAnswer[0].points : 0)
          })
        })
        return {
          ...p,
          points: totalPoints,
        }
      })
      .filter((p) => !p.deleted)
      .sort((a, b) => b.points - a.points)
      .map((player, index) => {
        let totalSpentTime = 0
        let totalAnswersCount = 0
        tasks.forEach((task) => {
          const answerTime = task.subtasks[0].answers
            ?.filter((a) => a.userId === player.id && a.state !== AnswerStateEnum.REVISION)
            ?.map((a) => {
              return new Date(a.completedAt).getTime() - new Date(a.openedAt).getTime()
            })
          if (answerTime && answerTime.length > 0) {
            totalAnswersCount++
            totalSpentTime += answerTime[0] / 1000
          }
        })
        const status = getPlayerStateOverview(player.id, tasks, gameData)
        return {
          rank: index + 1,
          nickName: player.nickName,
          email: player.email,
          teamMemberNames: player.teamMemberNames,
          points: player.points,
          correctPercentage: Math.min((100 * player.points) / gameData.maxPoints, 100),
          gameDuration: totalSpentTime,
          completionRate: Math.ceil(Math.min((100 * totalAnswersCount) / tasks.length, 100)),
          awardedBadges: player.badges,
          overallCompleted: status.gameCompleted,
          createdAt: player.createdAt,
          completedAt: player.completedAt,
        }
      })
      .filter((p) => !playerFilters.badgeId || p.awardedBadges.findIndex((b) => b.id === playerFilters.badgeId) > -1)
      .filter((p) => playerFilters.completion !== 'completed' || p.completionRate === 100 || p.overallCompleted)
      .filter((p) => playerFilters.completion !== 'uncompleted' || (p.completionRate < 100 && !p.overallCompleted))

    setPlayerCount(playerData.length)
    setLastPage(Math.ceil(playerData.length / pageSize))

    return paginate(playerData, pageSize, currentPage, playerSortBy, playerSortDir) as PlayerDataRow[]
  }, [
    gameData,
    tasks,
    playersInDateRange,
    pageSize,
    currentPage,
    playerSortBy,
    playerSortDir,
    playerFilters.badgeId,
    playerFilters.completion,
  ])

  const summaryData = useMemo((): GameReportSummaryData | undefined => {
    if (!gameData || !tasks) {
      return undefined
    }
    const playerCount = playersInDateRange.filter((p) => !p.deleted).length
    const playersNotFinished: string[] = []
    const playerStatuses = playersInDateRange
      .filter((p) => !p.deleted)
      .map((player) => {
        const temp = getPlayerStateOverview(player.id, tasks, gameData)
        if (!temp.gameCompleted) {
          playersNotFinished.push(player.nickName)
        }
        return temp
      })
    const playersCompletedCount = playerStatuses.filter((ps) => ps.gameCompleted).length
    const averageScore =
      playersInDateRange
        .filter((p) => !p.deleted)
        .map((p) => {
          //Need to override the player's points here as they are not updated in poeple players realtime
          let totalPoints = 0
          tasks.forEach((task) => {
            task.subtasks.forEach((subtask) => {
              const playerAnswer = subtask.answers?.filter((a) => a.userId === p.id)
              totalPoints = totalPoints + (playerAnswer && playerAnswer[0] ? playerAnswer[0].points : 0)
            })
          })
          return totalPoints
        })
        .reduce((acc, curr) => {
          return acc + curr
        }, 0) / playerCount
    const completionRate = (100 * playersCompletedCount) / playerCount
    let mostDifficultTask = { name: '', correctPercentage: 100, averageTime: 0 }
    let mostSuccesfulTask = { name: '', correctPercentage: 0, averageTime: 0 }
    const awardedBadges: BadgeInfo[] = []
    tasks
      .filter((t) => t.type !== ExerciseType.ExploreExercise)
      .filter((t) => {
        const answeredByFilterUser =
          (playerFilters.startDate === undefined && playerFilters.endDate === undefined) ||
          (t.subtasks[0].answers?.filter((a) => activePlayerIds.includes(a.userId)) ?? []).length > 0
        return answeredByFilterUser
      })
      .forEach((task) => {
        task.subtasks[0].answers
          ?.filter((a) => activePlayerIds.includes(a.userId))
          .forEach((a) => {
            if (a.badge) {
              const ind = awardedBadges.findIndex((b) => b.id === a.badge?.id)
              if (ind > -1) {
                if (!awardedBadges[ind].userIds.includes(a.userId)) {
                  awardedBadges[ind].count++
                  awardedBadges[ind].userIds.push(a.userId)
                }
              } else {
                awardedBadges.push({ ...a.badge, count: 1, userIds: [a.userId] })
              }
            }
          })
        let taskMaxTotalPoints = 0
        let allPlayersTotalPoints = 0
        task.subtasks.forEach((subtask) => {
          subtask.answers
            ?.filter((a) => activePlayerIds.includes(a.userId))
            .forEach((a) => {
              allPlayersTotalPoints += a.points
              taskMaxTotalPoints +=
                subtask.type === ExerciseType.PollExercise && subtask.data.hasTotalPoints
                  ? subtask.data.totalPoints
                  : subtask.maxPoints
            })
        })
        const sumCompletionTime = getSumCompletionTime(task, activePlayerIds)
        const answerCount = task.subtasks[0].answers?.filter((a) => activePlayerIds.includes(a.userId)).length ?? 1
        const completedPercentage = Math.ceil((100 * allPlayersTotalPoints) / taskMaxTotalPoints)
        if (completedPercentage < mostDifficultTask.correctPercentage) {
          mostDifficultTask.name = task.name
          mostDifficultTask.correctPercentage = completedPercentage
          mostDifficultTask.averageTime = Math.ceil(sumCompletionTime / 1000 / answerCount)
        }
        if (completedPercentage > mostSuccesfulTask.correctPercentage) {
          mostSuccesfulTask.name = task.name
          mostSuccesfulTask.correctPercentage = completedPercentage
          mostSuccesfulTask.averageTime = Math.ceil(sumCompletionTime / 1000 / answerCount)
        }
      })

    return {
      gameName: gameData.name,
      completionRate,
      averageScore,
      playerCount,
      mostDifficultTask,
      mostSuccesfulTask,
      playersNotFinished,
      awardedBadges,
    }
  }, [gameData, tasks, playersInDateRange, playerFilters.startDate, playerFilters.endDate, activePlayerIds])

  const next = useCallback(() => {
    if (loading) {
      return
    }

    setCurrentPage((currentState) => currentState + 1)
  }, [loading])

  const prev = useCallback(() => {
    if (loading) {
      return
    }

    setCurrentPage((currentState) => currentState - 1)
  }, [loading])

  const selectPageSize = useCallback(
    (newPageSize: number) => {
      if (loading) {
        return
      }

      setCurrentPage(1)
      setPageSize(newPageSize)
    },
    [loading],
  )

  const setPage = useCallback(
    (newPage: number) => {
      if (loading) {
        return
      }

      setCurrentPage(newPage)
    },
    [loading],
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [playerFilters])

  useEffect(() => {
    const calculateAllPageOptions = Array.from({ length: lastPage }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}`,
    }))
    setAllPageOptions(calculateAllPageOptions)
  }, [lastPage])

  const setPlayerSort = useCallback((sortBy: PlayerSortOption, sortDir: ReportSortDir) => {
    setPlayerSortBy(sortBy)
    setPlayerSortDir(sortDir)
    setCurrentPage(1)
  }, [])

  const setTaskSort = useCallback((sortBy: TaskSortOption, sortDir: ReportSortDir) => {
    setTaskSortBy(sortBy)
    setTaskSortDir(sortDir)
  }, [])

  return (
    <GameReportDataContext.Provider
      value={{
        summaryData,
        currentPage,
        pageSize,
        pageData,
        tasksData,
        lastPage,
        allPageOptions,
        loading,
        playerFilters,
        playerSortBy,
        playerSortDir,
        taskSortBy,
        taskSortDir,
        setPage,
        selectPageSize,
        next,
        prev,
        setPlayerFilters,
        setPlayerSort,
        setTaskSort,
        playerCount,
        getAllPlayerDataRows,
      }}
    >
      {children}
    </GameReportDataContext.Provider>
  )
}

export const useReportData = () => {
  const context = useContext(GameReportDataContext)

  return context
}
